import { HttpContextToken, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable, tap } from 'rxjs';
import { BYPASS_ERROR_HANDLER_FOR_GRAPHQL_ERROR_CODES } from 'shared/abstract/bypass-error-link';
import { unescapeJsonString } from 'shared/util/unescapeJsonString';
import { ToastService } from '../services';

// This should be set to an array of values or a string/number
export const BYPASS_ERROR_HANDLER_FOR_ERROR_CODES = new HttpContextToken(() => []);

export const BYPASS_PARTIAL_SUCCESS_HANDLER = new HttpContextToken(() => false);

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
    constructor(
        private toastService: ToastService,
        private _sanitizer: DomSanitizer,
    ) {}

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        return next.handle(request).pipe(
            tap({
                next: (response: any) => {
                    if (response instanceof HttpResponse && request.url.toLowerCase().includes('graphql') && response.body.errors) {
                        response.body.errors.forEach((error: any) => {
                            if (error.extensions?.code === 'authorization') {
                                // We no longer need this, but I'm leaving it in, so we can easily
                                // know where to extend the auth error code in the future
                                // this.toastService.showToast("Authorization error", 'error');
                            } else {
                                try {
                                    const unescapedErrorObject = unescapeJsonString(error.message);
                                    let parsedErrorObject = JSON.parse(unescapedErrorObject);
                                    if (typeof parsedErrorObject === 'string') {
                                        parsedErrorObject = JSON.parse(parsedErrorObject);
                                    }

                                    if (parsedErrorObject?.Message.length > 0) {
                                        this.toastService.showToast(parsedErrorObject.Message ?? 'An error occurred', 'error');
                                    }
                                } catch {
                                    this.toastService.showToast('An error occurred', 'error');
                                }
                            }
                        });
                    }
                    // Handle 207 Multi-Status (Partial Success) responses
                    if (response instanceof HttpResponse && response.status === 207) {
                        if (!shouldPartialSuccessHandlerBeBypassed(request)) {
                            this.handlePartialSuccess(response);
                        }
                    }
                },
                error: (error: any) => {
                    if (error.status === 207 && error.name === 'HttpErrorResponse') {
                        if (!shouldPartialSuccessHandlerBeBypassed(request)) {
                            const message = error.error?.text || error.error || 'Partial success with parsing error';
                            this.toastService.showToast(message, 'warning');
                        }
                        return; // Don't process further
                    }

                    // We don't wish to handle 401 (Unauthorized) and 456 (invalid token)
                    // since the token interceptor handles that
                    // We also don't wish to handle graphQL 400 (Bad Request)
                    // Every independent endpoint will handle its error messages so that translations can be used
                    if (
                        error.status >= 400 &&
                        ![401, 456, 403, 412].includes(error.status) &&
                        !request.url.toLowerCase().includes('graphql')
                    ) {
                        if (!shouldErrorHandlerBeBypassed(request, error.status)) {
                            if (typeof error.error === 'string' || typeof error.error?.Error === 'string') {
                                this.toastService.showToast(typeof error.error === 'string' ? error.error : error.error?.Error, 'error');
                            } else {
                                this.toastService.showToast(error.message, 'error');
                            }
                        }
                    }

                    // We also don't wish to handle graphQL 401 (Unauthorized)
                    // We also don't wish to handle graphQL 420 (PASSWORD_CHANGE_REQUIRED)
                    // We also don't wish to handle graphQL 423 (ACCOUNT_DISABLED)
                    // We also don't wish to handle graphQL 456 (invalid token)
                    if (![401, 420, 423, 456].includes(error.status) && request.url.toLowerCase().includes('graphql')) {
                        if (!BYPASS_ERROR_HANDLER_FOR_GRAPHQL_ERROR_CODES.includes(error.status)) {
                            try {
                                if (error.error?.errors?.length) {
                                    for (let i = 0; i < error.error.errors.length; i++) {
                                        const currentError = error.error.errors[i];
                                        if (currentError) {
                                            const parsedCurrentError = JSON.parse(
                                                currentError.message.replace(/\\{1,2}"/g, '"').replace(/^"|"$/g, ''),
                                            );

                                            this.toastService.showToast(parsedCurrentError.Message ?? 'An error occurred', 'error');
                                        }
                                    }
                                } else if (getValidationError(error.error.data, 'VALIDATION_FAILED')) {
                                    const validationError = getValidationError(error.error.data, 'VALIDATION_FAILED');
                                    this.toastService.showToast(validationError.message ?? 'An error occurred', 'error');
                                } else {
                                    const unescapedErrorObject = unescapeJsonString(error.message);
                                    const parsedErrorObject = JSON.parse(unescapedErrorObject);

                                    if (parsedErrorObject?.Message.length > 0) {
                                        this.toastService.showToast(parsedErrorObject.Message ?? 'An error occurred', 'error');
                                    }
                                }
                            } catch {
                                this.toastService.showToast('An error occurred', 'error');
                            }
                        }
                    }
                },
            }),
        );
    }

    private handlePartialSuccess(response: HttpResponse<any>): void {
        try {
            const body = response.body;

            // Handle different 207 response formats based on your API structure
            // Option 1: If the API returns errors in a specific format
            if (body?.errors && Array.isArray(body.errors)) {
                body.errors.forEach((error: any) => {
                    const message = error.message || error.Message || 'Partial success - some operations failed';
                    this.toastService.showToast(message, 'warning');
                });
            }
            // Option 2: If the API returns a results array with individual status codes
            else if (body?.results && Array.isArray(body.results)) {
                const failedResults = body.results.filter((result: any) => result.status >= 400 || result.success === false);

                if (failedResults.length > 0) {
                    failedResults.forEach((result: any) => {
                        const message = result.message || result.error || 'Operation failed';
                        this.toastService.showToast(message, 'warning');
                    });
                }
            }
            // Option 3: Generic partial success message
            else {
                this.toastService.showToast('Some operations completed with warnings or errors', 'warning');
            }
        } catch (error) {
            // Fallback if parsing fails
            this.toastService.showToast('Partial success - some operations may have failed', 'warning');
        }
    }
}

function shouldErrorHandlerBeBypassed(request: HttpRequest<any>, statusCode: number): boolean {
    let requestContextBypassErrors: string | number | (string | number)[] = request.context.get(BYPASS_ERROR_HANDLER_FOR_ERROR_CODES);

    if (!Array.isArray(requestContextBypassErrors)) {
        if (requestContextBypassErrors) {
            requestContextBypassErrors = [requestContextBypassErrors];
        } else {
            requestContextBypassErrors = [];
        }
    }

    return requestContextBypassErrors.find((item) => item == statusCode) !== undefined;
}

function shouldPartialSuccessHandlerBeBypassed(request: HttpRequest<any>): boolean {
    return request.context.get(BYPASS_PARTIAL_SUCCESS_HANDLER);
}

function getValidationError(currentNode: any, statusCodeToFind: string) {
    if (currentNode === null || typeof currentNode !== 'object') {
        return undefined;
    }

    const obj = currentNode as Record<string, unknown>;
    const code = obj['statusCode'];

    if (typeof code === 'string' && code.includes(statusCodeToFind)) {
        const msg = obj['message'];
        return {
            statusCode: code,
            message: typeof msg === 'string' ? msg : undefined,
        };
    }

    for (const v of Object.values(obj)) {
        const foundError: any = getValidationError(v, statusCodeToFind);
        if (foundError) {
            return foundError;
        }
    }

    return undefined;
}
